import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { BookOpenText, Download } from "lucide-react";
import { useSyncExternalStore } from "react";

import { ApiError, downloadPdf } from "../api/client";
import { Callout } from "../components/Callout";
import { Presence } from "../components/presence";
import { Button, type ButtonSize, type ButtonVariant } from "../components/ui";
import { Waiting } from "../components/Waiting";
import { type Lang, t, useLang } from "../i18n/i18n";
import { licenceStore } from "../state/licenceScope";
import { useUi } from "../state/ui";

/** Guide de prise en main (demande d'Alexis du 01/11/2026) : le PDF du parcours utilisateur, de la licence à
 *  l'analyse après action, dans la langue de l'interface. Servi par l'API à tout poste activé, licence d'essai
 *  comprise (`GET /api/guide/{fr|en}`, ce n'est pas un export du dossier) ; téléchargé comme les autres PDF de
 *  l'application (`downloadPdf` : rien n'est enregistré si la réponse n'est pas un PDF).
 *
 *  Où le trouver : l'encart de l'accueil juste après l'activation (jusqu'au premier téléchargement ou à sa
 *  fermeture, retenu pour la licence du poste), l'en-tête de « Toute l'aide », le menu des réglages, le pied de
 *  l'accueil et la dernière étape de la visite « Prise en main ». */

export function guidePath(language: Lang): string {
  return `/api/guide/${language}`;
}

/** Encart de l'accueil vu (téléchargé ou fermé) pour la licence de ce poste. */
export const GUIDE_CARD_KEY = "generex:guide-encart";
const listeners = new Set<() => void>();
// Fermé pendant cette session : vaut même sans stockage du navigateur (navigation privée, stockage plein).
let dismissedHere = false;

function cardDismissed(): boolean {
  return dismissedHere || licenceStore.get(GUIDE_CARD_KEY) === "1";
}

export function dismissGuideCard(): void {
  dismissedHere = true;
  licenceStore.set(GUIDE_CARD_KEY, "1");
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Téléchargement du guide dans la langue de l'interface ; l'avis dit le nom du fichier, ou l'échec. */
export function useGuideDownload() {
  const language = useLang();
  const mutation = useMutation({
    mutationFn: () => downloadPdf(guidePath(language)),
    onSuccess: (name) => {
      dismissGuideCard();
      useUi.getState().notify({ text: t("Guide de prise en main téléchargé"), detail: name, tone: "ok" });
    },
    onError: (error) =>
      useUi.getState().notify({
        text: t("Le guide n'a pas pu être téléchargé"),
        detail: error instanceof ApiError ? error.message : t("Réessayez dans un instant."),
        tone: "error",
      }),
  });
  return { download: () => mutation.mutate(), pending: mutation.isPending };
}

/** Bouton « Télécharger le guide (PDF) » : `label` court ou long selon la place. */
export function GuideDownloadButton({
  variant = "secondary",
  size,
  label = t("Télécharger"),
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  label?: string;
  className?: string;
}) {
  const { download, pending } = useGuideDownload();
  return (
    <Button variant={variant} size={size} className={className} disabled={pending} onClick={download}>
      <Download size={14} aria-hidden /> {pending ? <Waiting>{t("Téléchargement")}</Waiting> : label}
    </Button>
  );
}

/** Encart de l'accueil, juste après l'activation de la licence : « Votre guide de prise en main ». */
export function GuideWelcomeCallout({ className }: { className?: string }) {
  const dismissed = useSyncExternalStore(subscribe, cardDismissed, cardDismissed);
  return (
    <Presence show={!dismissed}>
      {!dismissed && (
        <Callout
          tone="info"
          icon={BookOpenText}
          title={t("Votre guide de prise en main")}
          className={className}
          action={<GuideDownloadButton size="sm" label={t("Télécharger le guide (PDF)")} />}
          onClose={dismissGuideCard}
        >
          {t("Tout Generex Web pas à pas, de la licence à l'analyse après action, en PDF illustré. Il reste à portée de main dans l'aide, les réglages et le pied de l'accueil.")}
        </Callout>
      )}
    </Presence>
  );
}

/** En tête de « Toute l'aide » : le guide, à télécharger. */
export function GuideHelpCard({ className }: { className?: string }) {
  return (
    <div className={clsx("flex items-center gap-3 rounded-lg border border-line bg-canvas px-3 py-2.5", className)}>
      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent">
        <BookOpenText size={16} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-ui font-semibold text-ink">{t("Guide de prise en main")}</p>
        <p className="text-meta text-ink-muted">{t("Tout Generex Web pas à pas, en PDF illustré.")}</p>
      </div>
      <GuideDownloadButton size="sm" />
    </div>
  );
}
